import React from 'react';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import cx from 'classnames';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { useClient } from '@plone/volto/hooks/client/useClient';
import {
  CheckIcon,
  ClockIcon,
  InfoIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import {
  dataCompleta,
  descreverNotificacao,
  rotuloStatus,
  tempoRelativoCurto,
  tomDaNotificacao,
} from 'volto-ministerio-publico-federal/helpers/Notificacoes';
import type { Notificacao } from 'volto-ministerio-publico-federal/helpers/Notificacoes';
import StatusPill from './StatusPill';
import type { TomPilula } from './StatusPill';

type Props = {
  item: Notificacao;
  /** O uid ainda está na Minha fila: só então há "Requer sua ação". */
  naFila: boolean;
  onAbrir: (item: Notificacao) => void;
  onAlternarLida: (item: Notificacao) => void;
};

const ICONES = {
  acao: ClockIcon,
  sucesso: CheckIcon,
  alerta: WarningIcon,
  info: InfoIcon,
};

/** Item do drawer: só o essencial — status, ação e o conteúdo afetado. */
const PainelItem = ({ item, naFila, onAbrir, onAlternarLida }: Props) => {
  const isClient = useClient();
  const token = useSelector((state: any) => state.userSession?.token);
  const usuarioAtual: string | null = token
    ? (jwtDecode(token) as any).sub
    : null;

  // Só "ação" se ainda está na fila; senão a ação já foi resolvida por alguém.
  const tomBruto = tomDaNotificacao(item);
  const tom: TomPilula = naFila
    ? 'acao'
    : tomBruto === 'acao'
      ? 'info'
      : tomBruto;
  const Icone = ICONES[tom as keyof typeof ICONES];
  const destino = flattenToAppURL(item.url);
  const status = rotuloStatus(item, naFila);
  const acao = descreverNotificacao(item, usuarioAtual);

  return (
    <li className="mpf-notificacoes__painel-item">
      <span
        className={cx(
          'mpf-notificacoes__painel-icone',
          `mpf-notificacoes__painel-icone--${tom}`,
        )}
        aria-hidden="true"
      >
        <Icone size={16} weight="bold" />
      </span>

      <div className="mpf-notificacoes__painel-corpo">
        <div className="mpf-notificacoes__painel-linha">
          {status && <StatusPill tom={tom}>{status}</StatusPill>}
          <span className="mpf-notificacoes__painel-meta">
            <time
              className="mpf-notificacoes__painel-tempo"
              dateTime={item.created}
              title={isClient ? dataCompleta(item.created) : undefined}
            >
              {isClient ? tempoRelativoCurto(item.created, Date.now()) : ''}
            </time>
            <button
              type="button"
              className="mpf-notificacoes__painel-ponto-botao"
              onClick={() => onAlternarLida(item)}
              aria-label={
                item.lida
                  ? `Marcar "${item.titulo}" como não lida`
                  : `Marcar "${item.titulo}" como lida`
              }
              title={item.lida ? 'Marcar como não lida' : 'Marcar como lida'}
            >
              <span
                className={cx('mpf-notificacoes__painel-ponto', {
                  'mpf-notificacoes__painel-ponto--lida': item.lida,
                })}
                aria-hidden="true"
              />
            </button>
          </span>
        </div>

        {item.disponivel ? (
          <UniversalLink
            href={destino}
            className="mpf-notificacoes__painel-acao"
            onClick={() => onAbrir(item)}
          >
            {acao}
          </UniversalLink>
        ) : (
          <p className="mpf-notificacoes__painel-acao">{acao}</p>
        )}
        <p className="mpf-notificacoes__painel-titulo">
          {item.titulo}
          {!item.disponivel && ' (indisponível)'}
        </p>
      </div>
    </li>
  );
};

export default PainelItem;
